import React, { useState, useEffect } from "react";
import axios from "axios";
import { Tabs, Tab, Box } from "@mui/material";
import { DataGridPremium, GridToolbar } from "@mui/x-data-grid-premium";

const DetailedAgentCampaign = ({ startDate, endDate, token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const handleRowExpansion = (id) => {
    const newExpandedRows = new Set(expandedRows);
    if (expandedRows.has(id)) {
      newExpandedRows.delete(id);
    } else {
      newExpandedRows.add(id);
    }
    setExpandedRows(newExpandedRows);
  };

  const getVisibleRows = (rows) => {
    if (!rows) return [];
    return rows.filter(
      (row) =>
        row.parent_id === null ||
        expandedRows.has(row.parent_id) ||
        (row.grandparent_id &&
          expandedRows.has(row.grandparent_id) &&
          expandedRows.has(row.parent_id)),
    );
  };

  const handleChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/reports/CallCentreSalesReports/DetailedAgentCampaignSBIReport?startDate=${startDate}&endDate=${endDate}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        // Transform the data for three-level hierarchy
        const transformedData = [];
        let currentId = 1;

        response.data.data.forEach((team) => {
          // Level 1: Team
          const teamRow = {
            ...team,
            id: currentId,
            parent_id: null,
            grandparent_id: null,
            row_type: "team",
          };
          delete teamRow.agent_team_details;
          transformedData.push(teamRow);

          // Level 2: Agent Team
          team.agent_team_details?.forEach((agentTeam) => {
            const agentTeamRow = {
              ...agentTeam,
              id: ++currentId,
              parent_id: teamRow.id,
              grandparent_id: null,
              row_type: "agent_team",
            };
            delete agentTeamRow.agent_details;
            transformedData.push(agentTeamRow);

            // Level 3: Agent Details
            agentTeam.agent_details?.forEach((agent) => {
              transformedData.push({
                ...agent,
                id: ++currentId,
                parent_id: agentTeamRow.id,
                grandparent_id: teamRow.id,
                row_type: "agent",
              });
            });
          });
          currentId++;
        });

        setData({ ...response.data, data: transformedData });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (startDate && endDate) {
      fetchData();
    }
  }, [startDate, endDate, token]);

  const salesColumns = [
    { field: "manager_name", headerName: "Manager", flex: 1 },
    { field: "agent_name", headerName: "Agent", flex: 1 },
    { field: "campaign", headerName: "Campaign", flex: 1.5 },
    { field: "total_sales", headerName: "Total Sales", flex: 1 },
    { field: "sales", headerName: "Sales", flex: 1 },
    { field: "incomplete", headerName: "Incomplete", flex: 1 },
    { field: "pended", headerName: "Pended", flex: 1 },
    { field: "staged", headerName: "Staged", flex: 1 },
    { field: "cancelled", headerName: "Cancelled", flex: 1 },
  ];

  const biColumns = [
    { field: "manager_name", headerName: "Manager", flex: 1 },
    { field: "agent_name", headerName: "Agent", flex: 1 },
    { field: "campaign", headerName: "Campaign", flex: 1.5 },
    {
      field: "total_bi",
      headerName: "Total BI",
      flex: 1,
      valueGetter: (params) => parseFloat(params.row.total_bi).toFixed(2),
    },
    {
      field: "sales_bi",
      headerName: "Sales BI",
      flex: 1,
      valueGetter: (params) => parseFloat(params.row.sales_bi).toFixed(2),
    },
  ];

  const hoursColumns = [
    { field: "manager_name", headerName: "Manager", flex: 1 },
    { field: "agent_name", headerName: "Agent", flex: 1 },
    { field: "campaign", headerName: "Campaign", flex: 1.5 },
    {
      field: "campaign_hours",
      headerName: "Campaign Hours",
      flex: 1,
      valueGetter: (params) =>
        params.row.campaign_hours
          ? parseFloat(params.row.campaign_hours).toFixed(2)
          : "",
    },
  ];

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!data || data.data.length === 0) return <h3>No Data Available</h3>;

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <Tabs value={tabValue} onChange={handleChange} aria-label="report tabs">
        <Tab label="Sales" />
        <Tab label="Hours" />
        <Tab label="BI" />
      </Tabs>

      <Box sx={{ mt: 2 }}>
        {tabValue === 0 && (
          <DataGridPremium
            autoHeight
            rows={getVisibleRows(data.data)}
            columns={[
              {
                field: "__expand",
                headerName: "",
                width: 50,
                renderCell: (params) => {
                  const hasChildren = data.data.some(
                    (row) => row.parent_id === params.row.id,
                  );
                  if (!hasChildren) return null;
                  return (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowExpansion(params.row.id);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "1.2rem",
                        color: "var(--mui-palette-secondary-main)",
                      }}
                    >
                      {expandedRows.has(params.row.id) ? "−" : "+"}
                    </button>
                  );
                },
              },
              ...salesColumns,
            ]}
            getRowId={(row) => row.id}
            slots={{ toolbar: GridToolbar }}
            // pagination
            // pageSize={pageSize}
            // onPageSizeChange={(newSize) => setPageSize(newSize)}
            // initialState={{
            //   pagination: { paginationModel: { pageSize: 10 } },
            // }}
            pageSizeOptions={[5, 10, 15, 20]}
            getRowClassName={(params) => `row-type-${params.row.row_type}`}
            sx={{
              "& .row-type-team": {
                // backgroundColor: "rgba(245, 245, 245, 0.8)",
                fontWeight: "bold",
              },
              "& .row-type-agent_team": {
                // backgroundColor: "rgba(250, 250, 250, 0.5)",
              },
              "& .row-type-agent": {
                // backgroundColor: "white",
              },
            }}
          />
        )}

        {tabValue === 1 && (
          <DataGridPremium
            autoHeight
            rows={getVisibleRows(data.data)}
            columns={[
              {
                field: "__expand",
                headerName: "",
                width: 50,
                renderCell: (params) => {
                  const hasChildren = data.data.some(
                    (row) => row.parent_id === params.row.id,
                  );
                  if (!hasChildren) return null;
                  return (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowExpansion(params.row.id);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "1.2rem",
                        color: "var(--mui-palette-secondary-main)",
                      }}
                    >
                      {expandedRows.has(params.row.id) ? "−" : "+"}
                    </button>
                  );
                },
              },
              ...hoursColumns,
            ]}
            getRowId={(row) => row.id}
            slots={{ toolbar: GridToolbar }}
            // pagination
            // pageSize={pageSize}
            // onPageSizeChange={(newSize) => setPageSize(newSize)}
            // initialState={{
            //   pagination: { paginationModel: { pageSize: 10 } },
            // }}
            // pageSizeOptions={[5, 10, 15, 20]}
            getRowClassName={(params) => `row-type-${params.row.row_type}`}
            sx={{
              "& .row-type-team": {
                // backgroundColor: "rgba(245, 245, 245, 0.8)",
                fontWeight: "bold",
              },
              "& .row-type-agent_team": {
                // backgroundColor: "rgba(250, 250, 250, 0.5)",
              },
              "& .row-type-agent": {
                // backgroundColor: "white",
              },
            }}
          />
        )}

        {tabValue === 2 && (
          <DataGridPremium
            autoHeight
            rows={getVisibleRows(data.data)}
            columns={[
              {
                field: "__expand",
                headerName: "",
                width: 50,
                renderCell: (params) => {
                  const hasChildren = data.data.some(
                    (row) => row.parent_id === params.row.id,
                  );
                  if (!hasChildren) return null;
                  return (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowExpansion(params.row.id);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "1.2rem",
                        color: "var(--mui-palette-secondary-main)",
                      }}
                    >
                      {expandedRows.has(params.row.id) ? "−" : "+"}
                    </button>
                  );
                },
              },
              ...biColumns,
            ]}
            getRowId={(row) => row.id}
            slots={{ toolbar: GridToolbar }}
            // pagination
            // pageSize={pageSize}
            // onPageSizeChange={(newSize) => setPageSize(newSize)}
            // initialState={{
            //   pagination: { paginationModel: { pageSize: 10 } },
            // }}
            // pageSizeOptions={[5, 10, 15, 20]}
            getRowClassName={(params) => `row-type-${params.row.row_type}`}
            sx={{
              "& .row-type-team": {
                // backgroundColor: "rgba(245, 245, 245, 0.8)",
                fontWeight: "bold",
              },
              "& .row-type-agent_team": {
                // backgroundColor: "rgba(250, 250, 250, 0.5)",
              },
              "& .row-type-agent": {
                // backgroundColor: "white",
              },
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default DetailedAgentCampaign;
